import React, { useEffect, useState, useCallback } from "react";
import { Search, Shield, BadgeCheck, Trash2, Users, CreditCard, MessageSquare, Activity,
         LogIn, Bug, Mail, Check, Wallet, Percent, Loader2, Ban, ShieldOff,
         MessagesSquare } from "lucide-react";
import { toast } from "sonner";
import { Panel, Skeleton, StatCard, Segmented } from "../components/ui";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { shortDate } from "../lib/format";
import { resetPricing } from "../lib/pricing";
import VerifiedBadge from "../components/VerifiedBadge";
import { useTranslation } from "react-i18next";
import { startImpersonation } from "../components/ImpersonationBanner";

const ROLES = ["user", "investor", "admin"];

export default function Admin() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [audit, setAudit] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState([]);
  const [errSummary, setErrSummary] = useState(null);
  const [invites, setInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [pcfg, setPcfg] = useState(null);
  const [payouts, setPayouts] = useState(null);
  const [dms, setDms] = useState(null);

  // Support view of the one conversation each Full Access member gets. The reset here
  // is the documented fix for "the investor never replied and I'm stuck" — it closes
  // the grant so the member can choose again, and lands in the audit log with both
  // identities, because it is an exception granted to a named person.
  const loadConversations = useCallback(() => {
    api.dmAdminOverview().then((r) => setDms(r.data.conversations || [])).catch(() => setDms([]));
  }, []);

  const resetConversation = async (row) => {
    if (!window.confirm(t("adminDm.resetConfirm", { member: row.member_name,
                                                    investor: row.investor_name }))) return;
    try {
      await api.dmAdminReset(row.member_id);
      toast.success(t("adminDm.reset"));
      loadConversations();
    } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
  };

  const loadUsers = useCallback(() => {
    api.adminUsers(q).then((r) => setUsers(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [q]);

  useEffect(() => {
    const timer = setTimeout(loadUsers, 300);
    return () => clearTimeout(timer);
  }, [loadUsers]);
  useEffect(() => { api.adminStats().then((r) => setStats(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (tab === "audit") api.adminAudit({ limit: 200 }).then((r) => setAudit(r.data)).catch(() => {});
    if (tab === "errors") api.adminErrors({ limit: 100 }).then((r) => setErrors(r.data)).catch(() => {});
    if (tab === "invites") api.listInvites().then((r) => setInvites(r.data)).catch(() => {});
    if (tab === "partner") {
      api.partnerSettings().then((r) => setPcfg(r.data)).catch(() => {});
      api.partnerPayouts().then((r) => setPayouts(r.data)).catch(() => {});
    }
    if (tab === "messages") loadConversations();
    // eslint-disable-next-line
  }, [tab]);

  // Admin settings are edited into a local draft and written only on Save. These
  // numbers decide what every investor gets paid; tabbing out of a field should
  // never be a live change to production. See rule 9 in CLAUDE.md.
  const [pdraft, setPdraft] = useState(null);
  const [psaving, setPsaving] = useState(false);
  useEffect(() => { setPdraft(pcfg ? JSON.parse(JSON.stringify(pcfg)) : null); }, [pcfg]);

  const pdirty = !!pdraft && !!pcfg && JSON.stringify(pdraft) !== JSON.stringify(pcfg);
  const editPartner = (patch) => setPdraft((d) => ({ ...d, ...patch }));
  const editShare = (tier, value) =>
    setPdraft((d) => ({ ...d, share_pct: { ...d.share_pct, [tier]: value } }));

  const savePartner = async () => {
    if (!pdirty || psaving) return;
    setPsaving(true);
    try {
      const { data } = await api.updatePartnerSettings({
        share_pct: pdraft.share_pct,
        subscription_gross_eur: Number(pdraft.subscription_gross_eur),
        full_gross_eur: Number(pdraft.full_gross_eur),
        early_access_minutes: Number(pdraft.early_access_minutes),
        vat_pct: Number(pdraft.vat_pct),
        min_payout_eur: Number(pdraft.min_payout_eur),
        enabled: !!pdraft.enabled,
        referral_credit_enabled: !!pdraft.referral_credit_enabled,
        referral_credit_days: Number(pdraft.referral_credit_days),
      });
      setPcfg(data);
      // The price is cached per browser session for the pages that quote it. Drop it
      // here, or the admin who just changed it is the one person still shown the old
      // number until they reload.
      resetPricing();
      toast.success(t("dialogs.saved"));
    } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
    finally { setPsaving(false); }
  };

  // Banning is two deliberate steps, not one dialog. Step one shows what it will
  // actually do — cancel a subscription, refund a real amount, block a name plus N
  // machines — and step two requires typing the email back. A mis-click is stopped by
  // the first; a mis-click on the *wrong row*, which is the one that costs money and
  // is impossible to spot afterwards, is stopped by the second.
  const [banTarget, setBanTarget] = useState(null);
  const [banStep, setBanStep] = useState(1);
  const [banReason, setBanReason] = useState("");
  const [banRefund, setBanRefund] = useState(true);
  const [banConfirm, setBanConfirm] = useState("");
  const [banBusy, setBanBusy] = useState(false);

  const openBan = async (u) => {
    setBanTarget({ ...u, preview: null });
    setBanStep(1); setBanReason(""); setBanRefund(true); setBanConfirm("");
    try {
      const { data } = await api.banPreview(u.id);
      setBanTarget((cur) => (cur && cur.id === u.id ? { ...cur, preview: data } : cur));
    } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
  };

  const confirmBan = async () => {
    if (banBusy || !banTarget) return;
    setBanBusy(true);
    try {
      const { data } = await api.banUser(
        banTarget.id, { reason: banReason.trim(), refund: banRefund }, banConfirm.trim());
      toast.success(data.refund?.refunded
        ? t("ban.doneRefunded", { amount: (data.refund.amount_eur || 0).toFixed(2) })
        : t("ban.done"));
      setBanTarget(null);
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
    finally { setBanBusy(false); }
  };

  const unban = async (u) => {
    if (!window.confirm(t("ban.liftConfirm", { email: u.email }))) return;
    try {
      await api.unbanUser(u.id);
      toast.success(t("ban.lifted"));
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
  };

  const markPaid = async (investorId) => {
    if (!window.confirm(t("adminPartner.markPaidConfirm"))) return;
    try {
      await api.markPayoutPaid(investorId);
      api.partnerPayouts().then((r) => setPayouts(r.data)).catch(() => {});
      toast.success(t("adminPartner.marked"));
    } catch { toast.error(t("dialogs.failed")); }
  };
  useEffect(() => { api.errorSummary().then((r) => setErrSummary(r.data)).catch(() => {}); }, []);

  // Support impersonation: exchange the code the user read out for a scoped session.
  // The account holder's own code is always required — an admin cannot enter anybody
  // without them reading it out, which is the whole consent model.
  const impersonate = async (who) => {
    const code = window.prompt(who
      ? t("admin.enterStudioCodePrompt", { email: who.email })
      : t("admin.impersonateCodePrompt"));
    if (!code) return;
    const reason = window.prompt(t("admin.impersonateReasonPrompt"));
    if (!reason) return;
    try {
      const { data } = await api.impersonate({ code, reason });
      if (who && data.target.id !== who.id) {
        toast.error(t("admin.codeBelongsToSomeoneElse"));
        return;
      }
      // No token to hand over: the impersonate response already set the session
      // cookie, so this is just the redirect.
      startImpersonation(data.target, data.write_approved, data.landing);
    } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
  };

  const sendInvite = async () => {
    if (!inviteEmail.trim()) return;
    try {
      const { data } = await api.createInvite({ email: inviteEmail.trim() });
      setInviteEmail("");
      api.listInvites().then((r) => setInvites(r.data)).catch(() => {});
      toast.success(data.emailed ? t("admin.inviteSent") : t("admin.inviteQueued"));
    } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
  };

  const resolveErr = async (id) => {
    try { await api.resolveError(id); setErrors((es) => es.filter((e) => e.id !== id)); }
    catch { toast.error(t("dialogs.failed")); }
  };

  const setRole = async (u, role) => {
    try {
      await api.adminSetRole(u.id, role);
      toast.success(t("admin.roleUpdated", { name: u.email, role }));
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
  };

  const toggleVerified = async (u) => {
    try {
      await api.adminSetInvestorFlags(u.id, { verified: !u.investor_verified });
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
  };

  const removeUser = async (u) => {
    // Typed confirmation: the API also requires the email back, so a mis-click on the
    // wrong row can't delete an account.
    const typed = window.prompt(t("admin.deletePrompt", { email: u.email }));
    if (!typed) return;
    try {
      await api.adminDeleteUser(u.id, typed);
      toast.success(t("admin.deleted"));
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="label">{t("admin.subtitle")}</span>
          <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tighter mt-1">{t("admin.title")}</h1>
        </div>
        <button className="btn-outline" onClick={() => impersonate(null)} data-testid="impersonate-btn">
          <LogIn size={16} /> {t("admin.impersonate")}
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats">
          <StatCard testid="admin-stat-users" label={t("admin.users")} icon={Users} value={stats.users} sub={t("admin.investorsCount", { count: stats.investors })} />
          <StatCard testid="admin-stat-subs" label={t("admin.subscriptions")} icon={CreditCard} value={stats.active_subscriptions} sub={t("admin.activeNow")} delay={0.05} />
          <StatCard testid="admin-stat-published" label={t("admin.published")} icon={BadgeCheck} value={stats.published_investors} sub={t("admin.liveProfiles")} delay={0.1} />
          <StatCard testid="admin-stat-pending" label={t("admin.pendingComments")} icon={MessageSquare} value={stats.pending_comments} sub={t("admin.awaitingReview")} delay={0.15} />
        </div>
      )}

      <Segmented
        options={[
          { value: "users", label: t("admin.tabUsers") },
          { value: "invites", label: t("admin.tabInvites") },
          { value: "errors", label: `${t("admin.tabErrors")}${errSummary?.unresolved ? ` (${errSummary.unresolved})` : ""}` },
          { value: "partner", label: t("admin.tabPartner") },
          { value: "messages", label: t("admin.tabMessages") },
          { value: "audit", label: t("admin.tabAudit") },
        ]}
        value={tab} onChange={setTab} testid="admin-tabs" fullWidthOnMobile
      />

      {tab === "partner" ? (
        <div className="space-y-5" data-testid="admin-partner">
          <Panel className="card p-5" testid="partner-settings">
            <div className="flex items-center gap-2 mb-1">
              <Percent size={17} className="text-brand-500" />
              <h3 className="font-heading font-semibold tracking-tight text-lg">{t("adminPartner.title")}</h3>
            </div>
            <p className="text-sm text-slate-500">{t("adminPartner.body")}</p>
            {!pcfg ? <Skeleton className="h-40 mt-4" /> : (
              <>
                <div className="mt-4 grid sm:grid-cols-2 gap-4">
                  {["bronze", "silver", "gold", "platinum"].map((tier) => (
                    <div key={tier}>
                      <label className="label">{t("adminPartner.shareFor", { tier: t(`tiers.${tier}`) })}</label>
                      <div className="flex items-center gap-2 mt-1">
                        <input type="number" min="0" max="90" className="input tabular"
                               data-testid={`share-${tier}`}
                               value={pdraft?.share_pct?.[tier] ?? ""}
                               onChange={(e) => editShare(tier, Number(e.target.value))} />
                        <span className="text-slate-400 text-sm">%</span>
                      </div>
                      {/* Both plans, because the same percentage pays out very
                          different money on a Pro and a Full Access subscription. */}
                      <div className="text-xs text-slate-400 mt-1 tabular space-y-0.5">
                        <div>{t("adminPartner.perSubPro", { amount: (pcfg.preview?.by_plan?.pro?.per_tier?.[tier] ?? 0).toFixed(2) })}</div>
                        <div>{t("adminPartner.perSubFull", { amount: (pcfg.preview?.by_plan?.full?.per_tier?.[tier] ?? 0).toFixed(2) })}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[["subscription_gross_eur", "priceGross"], ["full_gross_eur", "priceFull"],
                    ["vat_pct", "vat"], ["min_payout_eur", "minPayout"]].map(([key, label]) => (
                    <div key={key}>
                      <label className="label">{t(`adminPartner.${label}`)}</label>
                      <input type="number" step="0.01" className="input tabular mt-1" data-testid={`pcfg-${key}`}
                             value={pdraft?.[key] ?? ""}
                             onChange={(e) => editPartner({ [key]: Number(e.target.value) })} />
                    </div>
                  ))}
                </div>
                {/* The embargo lives here rather than on a feature-flags screen: it is
                    the reason the expensive plan is worth buying, so it belongs next to
                    the prices it justifies. */}
                <div className="mt-5">
                  <label className="label">{t("adminPartner.earlyAccess")}</label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="number" min="0" max="10080" className="input tabular max-w-[10rem]"
                           data-testid="pcfg-early_access_minutes"
                           value={pdraft?.early_access_minutes ?? ""}
                           onChange={(e) => editPartner({ early_access_minutes: Number(e.target.value) })} />
                    <span className="text-slate-400 text-sm">{t("adminPartner.minutes")}</span>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1.5">
                    {t("adminPartner.earlyAccessHint", {
                      hours: Math.round(((pdraft?.early_access_minutes ?? 0) / 60) * 10) / 10,
                    })}
                  </p>
                </div>
                <div className="mt-5 flex flex-wrap gap-5">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" data-testid="pcfg-enabled" checked={!!pdraft?.enabled}
                           onChange={(e) => editPartner({ enabled: e.target.checked })} />
                    {t("adminPartner.enabled")}
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" data-testid="pcfg-credit" checked={!!pdraft?.referral_credit_enabled}
                           onChange={(e) => editPartner({ referral_credit_enabled: e.target.checked })} />
                    {t("adminPartner.creditEnabled")}
                  </label>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-slate-500">{t("adminPartner.creditDays")}</span>
                    <input type="number" className="input tabular w-20 py-1" data-testid="pcfg-credit-days"
                           value={pdraft?.referral_credit_days ?? ""}
                           onChange={(e) => editPartner({ referral_credit_days: Number(e.target.value) })} />
                  </div>
                </div>
                <div className="mt-5 rounded-xl bg-slate-50 p-4 text-sm text-slate-600 space-y-1">
                  <div>{t("adminPartner.previewPro", { base: (pcfg.preview?.by_plan?.pro?.distributable_base ?? 0).toFixed(2) })}</div>
                  <div>{t("adminPartner.previewFull", { base: (pcfg.preview?.by_plan?.full?.distributable_base ?? 0).toFixed(2) })}</div>
                </div>

                {/* What each rank demands before it can be granted. Shown here because
                    approving a rank is a pricing decision: it permanently raises the
                    share on every referral that investor ever makes. */}
                {pcfg.rank_requirements && (
                  <div className="mt-4 overflow-x-auto">
                    <table className="w-full text-xs tabular">
                      <thead>
                        <tr className="text-slate-400 text-left">
                          <th className="py-1.5 pr-3 font-medium">{t("adminPartner.rank")}</th>
                          <th className="py-1.5 pr-3 font-medium">{t("adminPartner.reqPoints")}</th>
                          <th className="py-1.5 pr-3 font-medium">{t("adminPartner.reqAudience")}</th>
                          <th className="py-1.5 pr-3 font-medium">{t("adminPartner.reqReferrals")}</th>
                          <th className="py-1.5 font-medium">{t("adminPartner.reqMonths")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(pcfg.rank_requirements).map(([tier, r]) => (
                          <tr key={tier} className="border-t border-slate-100">
                            <td className="py-1.5 pr-3 text-slate-700">
                              {t(`tiers.${tier}`)}
                              {r.needs_approval && <span className="text-slate-400"> ·&nbsp;{t("adminPartner.byApproval")}</span>}
                            </td>
                            <td className="py-1.5 pr-3">{r.points}</td>
                            <td className="py-1.5 pr-3">{r.audience_points}</td>
                            <td className="py-1.5 pr-3">{r.converted_referrals}</td>
                            <td className="py-1.5">{r.months}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="mt-5 flex items-center gap-3 border-t border-slate-100 pt-4">
                  <button type="button" className="btn-primary" data-testid="pcfg-save"
                          disabled={!pdirty || psaving} onClick={savePartner}>
                    {psaving ? <Loader2 size={15} className="animate-spin" /> : null}
                    {t("adminPartner.save")}
                  </button>
                  <button type="button" className="btn-ghost text-sm" data-testid="pcfg-discard"
                          disabled={!pdirty || psaving}
                          onClick={() => setPdraft(JSON.parse(JSON.stringify(pcfg)))}>
                    {t("adminPartner.discard")}
                  </button>
                  {pdirty && (
                    <span className="text-xs text-amber-600 font-medium" data-testid="pcfg-dirty">
                      {t("adminPartner.unsaved")}
                    </span>
                  )}
                </div>
              </>
            )}
          </Panel>

          <Panel className="card p-5" testid="partner-payouts">
            <div className="flex items-center gap-2 mb-3">
              <Wallet size={17} className="text-brand-500" />
              <h3 className="font-heading font-semibold tracking-tight text-lg">{t("adminPartner.payouts")}</h3>
              {payouts && <span className="pill bg-slate-100 text-slate-500 tabular">€{payouts.total_pending_eur}</span>}
            </div>
            {!payouts || payouts.investors.length === 0 ? (
              <p className="text-sm text-slate-400 py-4 text-center">{t("adminPartner.noPayouts")}</p>
            ) : (
              <div className="divide-y divide-slate-50">
                {payouts.investors.map((i) => (
                  <div key={i.investor_id} className="py-3 flex items-center gap-3" data-testid={`payout-${i.investor_id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-800 truncate">{i.display_name}</div>
                      <div className="text-xs text-slate-400 truncate">{i.email}</div>
                    </div>
                    <span className="tabular font-semibold">€{i.pending_eur}</span>
                    <button className="btn-outline py-1 px-2 text-xs disabled:opacity-40"
                            disabled={!i.payable} onClick={() => markPaid(i.investor_id)}
                            data-testid={`markpaid-${i.investor_id}`}>
                      {i.payable ? t("adminPartner.markPaid") : t("adminPartner.belowMin")}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>
      ) : tab === "messages" ? (
        <Panel className="card p-5" testid="admin-dm">
          <div className="flex items-center gap-2 mb-1">
            <MessagesSquare size={17} className="text-brand-500" />
            <h3 className="font-heading font-semibold tracking-tight text-lg">{t("adminDm.title")}</h3>
          </div>
          <p className="text-sm text-slate-500">{t("adminDm.body")}</p>
          {dms === null ? (
            <Skeleton className="h-32 mt-4" />
          ) : dms.length === 0 ? (
            <p className="text-sm text-slate-400 py-8 text-center">{t("adminDm.none")}</p>
          ) : (
            <div className="mt-4 divide-y divide-slate-50">
              {dms.map((row) => (
                <div key={row.id} className="py-3 flex flex-wrap items-center gap-3"
                     data-testid={`admin-dm-${row.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-800 truncate">
                      {row.member_name} → {row.investor_name}
                    </div>
                    <div className="text-xs text-slate-400">
                      {t(`dm.status.${row.status}`)} · {t("adminDm.until", { date: shortDate(row.expires_at) })}
                      {" · "}{t("adminDm.messages", { count: row.message_count })}
                    </div>
                  </div>
                  <button className="btn-outline py-1 px-2 text-xs"
                          data-testid={`admin-dm-reset-${row.id}`}
                          onClick={() => resetConversation(row)}>
                    {t("adminDm.resetAction")}
                  </button>
                </div>
              ))}
            </div>
          )}
        </Panel>
      ) : tab === "invites" ? (
        <Panel className="card p-5" testid="admin-invites">
          <div className="flex items-center gap-2 mb-3">
            <Mail size={17} className="text-brand-500" />
            <h3 className="font-heading font-semibold tracking-tight text-lg">{t("admin.inviteTitle")}</h3>
          </div>
          <p className="text-sm text-slate-500 mb-3">{t("admin.inviteBody")}</p>
          <div className="flex gap-2">
            <input className="input py-2 text-sm" data-testid="invite-email" placeholder="investor@example.com"
                   value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} />
            <button className="btn-primary" onClick={sendInvite} data-testid="send-invite">{t("admin.invite")}</button>
          </div>
          <div className="mt-4 divide-y divide-slate-50">
            {invites.length === 0 && <p className="text-sm text-slate-400 py-4 text-center">{t("admin.noInvites")}</p>}
            {invites.map((i) => (
              <div key={i.link} className="py-3 flex items-center gap-3" data-testid={`invite-${i.email}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-800 truncate">{i.email}</div>
                  <div className="text-xs text-slate-400 truncate">{i.link}</div>
                </div>
                <span className={`pill ${i.status === "redeemed" ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>
                  {t(`admin.inviteStatus.${i.status}`)}
                </span>
                <button className="btn-outline py-1 px-2 text-xs"
                        onClick={() => { navigator.clipboard?.writeText(i.link); toast.success(t("admin.linkCopied")); }}>
                  {t("admin.copyLink")}
                </button>
              </div>
            ))}
          </div>
        </Panel>
      ) : tab === "errors" ? (
        <Panel className="card overflow-hidden" testid="admin-errors">
          {errSummary && (
            <div className="p-4 border-b border-slate-100 flex flex-wrap gap-5 text-sm">
              <span><span className="text-slate-400">{t("admin.errUnresolved")}: </span><b>{errSummary.unresolved}</b></span>
              <span><span className="text-slate-400">{t("admin.err24h")}: </span><b>{errSummary.last_24h}</b></span>
              <span><span className="text-slate-400">{t("admin.errBackend")}: </span><b>{errSummary.backend_last_24h}</b></span>
            </div>
          )}
          <div className="divide-y divide-slate-50">
            {errors.length === 0 && <p className="p-8 text-sm text-slate-400 text-center">{t("admin.noErrors")}</p>}
            {errors.map((e) => (
              <div key={e.id} className="p-4 flex items-start gap-3" data-testid={`error-${e.id}`}>
                <Bug size={15} className={`shrink-0 mt-0.5 ${e.source === "backend" ? "text-rose-500" : "text-amber-500"}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-800 break-words">{e.message}</div>
                  <div className="text-xs text-slate-400 mt-0.5">
                    {e.source} · {e.path} {e.user_email ? `· ${e.user_email}` : ""} · {shortDate(e.at)}
                  </div>
                </div>
                <button className="btn-outline py-1 px-2 text-xs shrink-0" onClick={() => resolveErr(e.id)}
                        data-testid={`resolve-${e.id}`}>
                  <Check size={13} /> {t("admin.resolve")}
                </button>
              </div>
            ))}
          </div>
        </Panel>
      ) : tab === "users" ? (
        <Panel className="card overflow-hidden" testid="admin-users">
          <div className="p-4 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input data-testid="admin-search" className="input pl-10 py-2.5 text-sm"
                     placeholder={t("admin.searchPlaceholder")} value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
          </div>

          {loading ? (
            <div className="p-4 space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16" />)}</div>
          ) : (
            <div className="divide-y divide-slate-50">
              {users.map((u) => (
                <div key={u.id} data-testid={`admin-user-${u.id}`}
                     className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-slate-900 truncate">{u.email}</span>
                      {u.is_domain_admin && (
                        <span className="pill bg-brand-50 text-brand-600 flex items-center gap-1">
                          <Shield size={11} /> {t("admin.staff")}
                        </span>
                      )}
                      {u.tier === "paid" && <span className="pill bg-emerald-50 text-emerald-600">{t("admin.paid")}</span>}
                      {u.investor_verified && <VerifiedBadge size={14} />}
                      {u.banned && (
                        <span className="pill bg-rose-50 text-rose-600 flex items-center gap-1">
                          <Ban size={11} /> {t("ban.banned")}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      {u.name} · {t("admin.joined")} {shortDate(u.created_at)}
                      {!u.totp_enabled && <span className="text-amber-600"> · {t("admin.no2fa")}</span>}
                      {u.banned && u.banned_reason && (
                        <span className="text-rose-500"> · {u.banned_reason}</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <select
                      data-testid={`role-select-${u.id}`}
                      className="input py-1.5 w-auto text-sm"
                      value={u.role}
                      disabled={u.is_domain_admin || u.id === user?.id}
                      onChange={(e) => setRole(u, e.target.value)}
                    >
                      {ROLES.map((r) => <option key={r} value={r}>{t(`admin.role.${r}`)}</option>)}
                    </select>
                    {u.role === "investor" && (
                      <button onClick={() => impersonate(u)} data-testid={`studio-${u.id}`}
                              disabled={u.banned}
                              className="p-2 rounded-lg text-slate-300 hover:text-brand-600 disabled:opacity-30 transition-colors"
                              title={t("admin.enterStudio")}>
                        <LogIn size={16} />
                      </button>
                    )}
                    {u.role === "investor" && (
                      <button onClick={() => toggleVerified(u)} data-testid={`verify-${u.id}`}
                              className={`p-2 rounded-lg transition-colors ${u.investor_verified ? "text-brand-600 bg-brand-50" : "text-slate-300 hover:text-brand-600"}`}
                              title={t("admin.toggleVerified")}>
                        <BadgeCheck size={16} />
                      </button>
                    )}
                    {u.banned ? (
                      <button onClick={() => unban(u)} data-testid={`unban-${u.id}`}
                              className="p-2 rounded-lg text-rose-500 bg-rose-50 hover:bg-rose-100 transition-colors"
                              title={t("ban.liftTitle")}>
                        <ShieldOff size={16} />
                      </button>
                    ) : (
                      <button onClick={() => openBan(u)}
                              disabled={u.id === user?.id || u.is_domain_admin || u.role === "admin"}
                              data-testid={`ban-${u.id}`}
                              className="p-2 rounded-lg text-slate-300 hover:text-rose-500 disabled:opacity-30 transition-colors"
                              title={t("ban.title")}>
                        <Ban size={16} />
                      </button>
                    )}
                    <button onClick={() => removeUser(u)} disabled={u.id === user?.id}
                            data-testid={`delete-${u.id}`}
                            className="p-2 rounded-lg text-slate-300 hover:text-rose-500 disabled:opacity-30 transition-colors">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      ) : (
        <Panel className="card overflow-hidden" testid="admin-audit">
          <div className="divide-y divide-slate-50">
            {audit.length === 0 && <p className="p-6 text-sm text-slate-400 text-center">{t("admin.noAudit")}</p>}
            {audit.map((a, i) => (
              <div key={i} className="px-4 py-3 flex items-start gap-3 text-sm">
                <Activity size={14} className="text-slate-300 mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <span className="font-medium text-slate-700">{a.action}</span>
                  <span className="text-slate-400"> · {a.actor_email || "system"}</span>
                  {a.detail && Object.keys(a.detail).length > 0 && (
                    <div className="text-xs text-slate-400 truncate">{JSON.stringify(a.detail)}</div>
                  )}
                </div>
                <div className="text-xs text-slate-400 shrink-0 tabular">{shortDate(a.at)}</div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {banTarget && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/40 backdrop-blur-sm p-0 sm:p-4"
             data-testid="ban-modal" role="dialog" aria-modal="true">
          <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-5 sm:p-6 space-y-4">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-xl bg-rose-50 text-rose-600 shrink-0"><Ban size={18} /></div>
                <div className="min-w-0">
                  <h3 className="font-heading font-semibold tracking-tight text-lg">{t("ban.title")}</h3>
                  <p className="text-sm text-slate-500 break-all">{banTarget.email}</p>
                </div>
              </div>

              {!banTarget.preview ? (
                <Skeleton className="h-32" />
              ) : banStep === 1 ? (
                <>
                  <div className="rounded-xl bg-slate-50 p-4 text-sm space-y-2">
                    <p className="font-medium text-slate-700">{t("ban.willDo")}</p>
                    <ul className="space-y-1 text-slate-600 text-[13px]">
                      <li>· {t("ban.willBlock", {
                        name: banTarget.preview.will_block.name || banTarget.email,
                        ips: banTarget.preview.will_block.ip_count,
                        devices: banTarget.preview.will_block.device_count,
                      })}</li>
                      <li>· {t("ban.willEndSessions")}</li>
                      {banTarget.preview.subscription_status && <li>· {t("ban.willCancel")}</li>}
                      {banTarget.preview.last_invoice && !banTarget.preview.last_invoice.already_refunded && (
                        <li>· {t("ban.willRefund", {
                          amount: (banTarget.preview.last_invoice.total_amount || 0).toFixed(2),
                          date: shortDate(banTarget.preview.last_invoice.issued_at),
                        })}</li>
                      )}
                      {banTarget.preview.last_invoice?.already_refunded && (
                        <li className="text-slate-400">· {t("ban.alreadyRefunded")}</li>
                      )}
                      {!banTarget.preview.last_invoice && (
                        <li className="text-slate-400">· {t("ban.noPayments")}</li>
                      )}
                    </ul>
                  </div>

                  {/* The rule only bites when a name matches *and* a machine or IP does.
                      With nothing recorded, this bans the account and stops there — say
                      so, rather than implying an enforcement that won't happen. */}
                  {banTarget.preview.will_block.ip_count === 0
                    && banTarget.preview.will_block.device_count === 0 && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3"
                       data-testid="ban-weak-warning">
                      {t("ban.noIdentities")}
                    </p>
                  )}

                  <div>
                    <label className="label">{t("ban.reasonLabel")}</label>
                    <textarea className="input mt-1 min-h-[80px] resize-y" data-testid="ban-reason"
                              value={banReason} onChange={(e) => setBanReason(e.target.value)}
                              placeholder={t("ban.reasonPlaceholder")} maxLength={500} />
                  </div>

                  {banTarget.preview.last_invoice && !banTarget.preview.last_invoice.already_refunded && (
                    <label className="flex items-start gap-2 text-sm cursor-pointer">
                      <input type="checkbox" className="mt-0.5" data-testid="ban-refund"
                             checked={banRefund} onChange={(e) => setBanRefund(e.target.checked)} />
                      <span className="text-slate-600">{t("ban.refundToggle")}</span>
                    </label>
                  )}

                  <div className="flex gap-2 justify-end pt-1">
                    <button className="btn-ghost" onClick={() => setBanTarget(null)}>{t("common.cancel")}</button>
                    <button className="btn-primary bg-rose-600 hover:bg-rose-700"
                            data-testid="ban-continue"
                            disabled={banReason.trim().length < 5}
                            onClick={() => setBanStep(2)}>
                      {t("ban.continue")}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="rounded-xl bg-rose-50 border border-rose-200 p-4 text-sm text-rose-900">
                    {t("ban.finalWarning")}
                  </div>
                  <div>
                    <label className="label">{t("ban.typeEmail")}</label>
                    <input className="input mt-1" data-testid="ban-confirm-email" autoComplete="off"
                           value={banConfirm} onChange={(e) => setBanConfirm(e.target.value)}
                           placeholder={banTarget.email} />
                  </div>
                  <div className="flex gap-2 justify-end pt-1">
                    <button className="btn-ghost" onClick={() => setBanStep(1)}>{t("common.back")}</button>
                    <button className="btn-primary bg-rose-600 hover:bg-rose-700"
                            data-testid="ban-confirm"
                            disabled={banBusy || banConfirm.trim().toLowerCase() !== (banTarget.email || "").toLowerCase()}
                            onClick={confirmBan}>
                      {banBusy ? <Loader2 size={15} className="animate-spin" /> : <Ban size={15} />}
                      {t("ban.confirm")}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
